import type { FetchParameters } from './fetch';

/**
 * Generic utility function to set any header value in fetch parameters
 * @param headerName - The name of the header to set
 * @param headerValue - The value to set for the header
 * @param fetchParams - The fetch parameters [resource, options]
 * @param options - Additional options
 * @param options.throwOnExisting - If true, throws an error when the header already exists
 * @param options.errorMessage - Custom error message to use when header exists
 * @returns Modified fetch parameters with the header set
 */
export function setHeader<T extends string = string>(
    headerName: T,
    headerValue: string,
    [resource, options = {}]: FetchParameters,
    {
        throwOnExisting = false,
        errorMessage = `Unexpected ${headerName} header encountered`,
    }: {
        throwOnExisting?: boolean;
        errorMessage?: string;
    } = {}
): FetchParameters {
    let hasHeaderBeenSet = false;

    // Handle Request instance with Headers
    if (resource instanceof Request && !options?.headers) {
        if (throwOnExisting && resource.headers.has(headerName)) {
            throw new Error(errorMessage);
        }
        resource.headers.set(headerName, headerValue);
        hasHeaderBeenSet = true;
    }

    // Handle options with Headers instance
    if (options?.headers instanceof Headers) {
        if (throwOnExisting && options.headers.has(headerName)) {
            throw new Error(errorMessage);
        }
        options.headers.set(headerName, headerValue);
    } else {
        // Handle options with plain object headers
        if (
            throwOnExisting &&
            options?.headers &&
            Reflect.has(options.headers as object, headerName)
        ) {
            throw new Error(errorMessage);
        }
        // Add the header if it hasn't been set on Request
        if (!hasHeaderBeenSet) {
            options.headers = {
                ...options?.headers,
                [headerName]: headerValue,
            };
        }
    }

    return [resource, options];
}
